<script setup lang="ts">
/**
 * UiBadge — pill / tag / status (inbox-120).
 *
 * Variants:
 *   default — нейтральный (var(--bg-elev) + var(--fg-muted))
 *   success — зелёный (var(--success-soft) + var(--success))
 *   warn    — оранжевый (var(--warning-soft) + var(--warning))
 *   danger  — красный (var(--danger-soft) + var(--danger))
 *
 * Prop `dot` — добавить 6px-кружок currentColor слева (для status-pill).
 *
 * CSS портом из docs/mockups/dashboard-v1.html + links-list.html
 * (.tag-pill / .status-pill / .badge).
 *
 * Usage:
 *   <UiBadge>3 теги</UiBadge>
 *   <UiBadge variant="success" dot>Active</UiBadge>
 *   <UiBadge variant="warn">Pending</UiBadge>
 *   <UiBadge variant="danger" dot>Expired</UiBadge>
 */

interface Props {
  variant?: 'default' | 'success' | 'warn' | 'danger';
  dot?: boolean;
}

withDefaults(defineProps<Props>(), {
  variant: 'default',
  dot: false,
});
</script>

<template>
  <span :class="['badge', `badge-${variant}`, { 'has-dot': dot }]">
    <span v-if="dot" class="badge-dot" aria-hidden="true" />
    <slot />
  </span>
</template>

<style scoped>
.badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border: 1px solid transparent;
  line-height: 1.4;
  vertical-align: middle;
}

.badge-default {
  background: var(--bg-elev);
  color: var(--fg-muted);
  border-color: var(--border);
}

.badge-success {
  background: var(--success-soft);
  color: var(--success);
}

.badge-warn {
  background: var(--warning-soft);
  color: var(--warning);
}

.badge-danger {
  background: var(--danger-soft);
  color: var(--danger);
}

.badge-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}
</style>
